import {
  Box,
  Checkbox,
  Flex,
  Heading,
  IconButton,
  ScrollArea,
  Text,
  Tooltip,
} from "@radix-ui/themes";
import { useMemo, useRef, useState } from "react";
import { Group } from "react-konva";
import { twMerge } from "tailwind-merge";
import { Canvas2DWrapper } from "../annotation/ui/canvas-2d-wrapper";
import { CanvasImage } from "./canvas-image";
import { useTrackedHotkeys } from "@/shared/hooks/useTrackedHotkeys";
import { DownloadIcon, UploadIcon } from "@radix-ui/react-icons";
import Input from "@/components/input";
import { InfoItem } from "@/components/info-item";
import { LegendToggle } from "./legend-toggle";
import { AnnotationBBox } from "./annotation-bbox";
import { PredictionBBox } from "./prediction-bbox";
import { ImportJsonButton } from "./import-json-button";
import Header from "@/shared/components/header";
import { NavigationItems } from "@/shared/types/navigation";
import { Dimensions } from "@/shared";

type TransactionsMap = Record<
  number,
  {
    predictions: {
      takeTransactions: any[];
      putTransactions: any[];
    };
    annotations: {
      takeTransactions: any[];
      putTransactions: any[];
    };
  }
>;

enum Colors {
  PREDICTION_STROKE = "rgba(176, 230, 76, 1)",
  PREDICTION_FILL = "rgba(176, 230, 76, 0.5)",
  ANNOTATION_STROKE = "red",
  ANNOTATION_FILL = "rgba(255, 0, 0, 0.5)",
}

export function SSCCVisualizerView() {
  const [gondolaChangeGroups, setGondolaChangeGroups] = useState<any[]>([]);
  const [modelOutputs, setModelOutputs] = useState<any[]>([]);
  const [selectedChangeGroup, setSelectedChangeGroup] = useState<any>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canvasDimensions, setCanvasDimensions] = useState<Dimensions>({
    width: 1000,
    height: 1000,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const baseURL = "https://d10ahk4z76fv3a.cloudfront.net/";
  const [isBefore, setIsBefore] = useState(false);
  const [visibility, setVisibility] = useState({
    prediction: true,
    annotation: true,
  });
  const [takeConfidence, setTakeConfidence] = useState("0.7");
  const [putConfidence, setPutConfidence] = useState("0.7");
  const [checkedMapping, setCheckedMapping] = useState<Record<string, boolean>>(
    {}
  );

  const focusElement = (index: number) => {
    const focusedElement = document.querySelector(
      `[data-index="${index}"]`
    ) as HTMLDivElement;

    focusedElement?.scrollIntoView({ block: "center" });
  };

  const toggleVisibility = (key: keyof typeof visibility) => {
    setVisibility((state) => ({
      ...state,
      [key]: !state[key],
    }));
  };

  useTrackedHotkeys(
    "1",
    () => {
      toggleVisibility("prediction");
    },
    {
      preventDefault: true,
    }
  );

  useTrackedHotkeys(
    "2",
    () => {
      toggleVisibility("annotation");
    },
    {
      preventDefault: true,
    }
  );

  useTrackedHotkeys(
    "up",
    () => {
      const nextIndex = Math.max(selectedIndex - 1, 0);
      setSelectedIndex(nextIndex);
      setSelectedChangeGroup(gondolaChangeGroups[nextIndex]);
      focusElement(nextIndex);
    },
    {
      preventDefault: true,
    }
  );
  useTrackedHotkeys(
    "down",
    () => {
      const nextIndex = Math.min(
        selectedIndex + 1,
        gondolaChangeGroups.length - 1
      );
      setSelectedIndex(nextIndex);
      setSelectedChangeGroup(gondolaChangeGroups[nextIndex]);
      focusElement(nextIndex);
    },
    {
      preventDefault: true,
    }
  );

  useTrackedHotkeys("right, d", () => {
    setIsBefore(false);
  });
  useTrackedHotkeys("left, a", () => {
    setIsBefore(true);
  });

  useTrackedHotkeys("g", () => {
    if (!selectedChangeGroup) return;

    setCheckedMapping({
      ...checkedMapping,
      [selectedChangeGroup.img_id]: true,
    });
  });
  useTrackedHotkeys("b", () => {
    if (!selectedChangeGroup) return;

    setCheckedMapping({
      ...checkedMapping,
      [selectedChangeGroup?.img_id]: false,
    });
  });

  const transactionsMap = useMemo(() => {
    return gondolaChangeGroups.reduce<TransactionsMap>((acc, annotation) => {
      const allTransactions = modelOutputs.filter(
        (output) => output.image_id === annotation.img_id
      );

      const predictions = {
        takeTransactions: allTransactions.filter(
          (output) => output.category_id === 1
        ),
        putTransactions: allTransactions.filter(
          (output) => output.category_id === 0
        ),
      };

      const annotations: {
        takeTransactions: { bbox: number[]; label: number }[];
        putTransactions: { bbox: number[]; label: number }[];
      } = {
        takeTransactions: [],
        putTransactions: [],
      };

      annotation.ann.bboxes.forEach((bbox: number[], index: number) => {
        const label = annotation.ann.labels[index];

        if (label === 1) {
          annotations.takeTransactions.push({
            bbox,
            label,
          });
        } else {
          annotations.putTransactions.push({
            bbox,
            label,
          });
        }
      });

      acc[annotation.img_id] = {
        predictions,
        annotations,
      };

      return acc;
    }, {});
  }, [gondolaChangeGroups, modelOutputs]);

  const downscalePoint = (x: number, y: number): [number, number] => {
    if (!selectedChangeGroup) return [0, 0];

    return [
      (x / selectedChangeGroup.width) * canvasDimensions.width,
      (y / selectedChangeGroup.height) * (canvasDimensions.width * (9 / 16)),
    ];
  };

  const calcPredictionsCount = (imageId: number) => {
    const imageTransactions = transactionsMap[imageId];
    const {
      predictions: { takeTransactions, putTransactions },
    } = imageTransactions;

    return takeTransactions.length + putTransactions.length;
  };

  const calcAnnotationCount = (imageId: number) => {
    const imageTransactions = transactionsMap[imageId];
    const {
      annotations: { takeTransactions, putTransactions },
    } = imageTransactions;

    return takeTransactions.length + putTransactions.length;
  };

  const onCheckedChange =
    (key: keyof typeof visibility) => (checked: boolean) =>
      setVisibility({
        ...visibility,
        [key]: checked,
      });

  const onImportGondolaChangeGroups = (value: any[]) => {
    setGondolaChangeGroups(value);
    setSelectedChangeGroup(value[0]);
    setSelectedIndex(0);
  };

  const onImportModelOutputs = (value: any) => {
    setModelOutputs(value);
  };

  function saveJSON(data: any, saveAs: string) {
    const stringified = JSON.stringify(data, null, 2);
    const blob = new Blob([stringified], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.download = saveAs + ".json";
    a.href = url;
    a.id = saveAs;
    document.body.appendChild(a);
    a.click();
    document.querySelector("#" + a.id)?.remove();
  }

  const onExport = () => {
    const output: {
      image_id: string;
      acceptance: boolean | null;
    }[] = [];

    gondolaChangeGroups.forEach((changeGroup) => {
      output.push({
        image_id: changeGroup.img_id,
        acceptance: checkedMapping[changeGroup.img_id] ?? null,
      });
    });

    saveJSON(output, "acceptance");
  };

  const filteredTakePredictions = transactionsMap[
    selectedChangeGroup?.img_id
  ]?.predictions.takeTransactions.filter(
    (output) => output.score >= takeConfidence
  );

  const filteredPutPredictions = transactionsMap[
    selectedChangeGroup?.img_id
  ]?.predictions.putTransactions.filter(
    (output) => output.score >= putConfidence
  );

  const totalTakePredictionCount =
    transactionsMap[selectedChangeGroup?.img_id]?.predictions.takeTransactions
      .length || 0;

  const totalPutAnnotationCount =
    transactionsMap[selectedChangeGroup?.img_id]?.predictions.putTransactions
      .length || 0;

  const annotationPutTransactions =
    transactionsMap[selectedChangeGroup?.img_id]?.annotations.putTransactions;

  const annotationTakeTransactions =
    transactionsMap[selectedChangeGroup?.img_id]?.annotations.takeTransactions;

  const filteredPredictionCount = () => {
    const filteredCount = isBefore
      ? filteredTakePredictions?.length || 0
      : filteredPutPredictions?.length || 0;

    const totalCount = isBefore
      ? totalTakePredictionCount
      : totalPutAnnotationCount;

    return `(${filteredCount}/${totalCount})`;
  };

  const filteredAnnotationCount = () => {
    const annotations =
      transactionsMap[selectedChangeGroup?.img_id]?.annotations;

    const takeCount = annotations?.takeTransactions?.length || 0;
    const putCount = annotations?.putTransactions?.length || 0;

    if (isBefore) {
      return `(${takeCount})`;
    }

    return `(${putCount})`;
  };

  return (
    <Flex className="h-screen" direction="column">
      <Header
        title={NavigationItems.SSCC.name}
        exclude={[NavigationItems.SSCC.id]}
      />
      <Flex flexGrow="1">
        <aside className="w-[350px] h-[calc(100dvh-2.5rem)] border-r">
          <Flex
            className="border-b border-[var(--gray-a4)]"
            gap="1"
            align="center"
            px="2"
          >
            <Heading size="2" my="2">
              Gondola Change Groups
            </Heading>
            <div className="grow" />
            <ImportJsonButton
              tooltip="Import gondola change groups from JSON"
              onImport={onImportGondolaChangeGroups}
            >
              <UploadIcon />
            </ImportJsonButton>
            <ImportJsonButton
              tooltip="Import model outputs from JSON"
              onImport={onImportModelOutputs}
            >
              <UploadIcon />
            </ImportJsonButton>
            <Tooltip content="Export as JSON">
              <IconButton
                variant="soft"
                size="1"
                color="gray"
                onClick={onExport}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Flex>
          <ScrollArea className="w-full h-[calc(100%-3rem)]">
            <Flex direction="column" className="w-full h-full">
              {gondolaChangeGroups.map((annotation, index) => (
                <Flex
                  data-index={index}
                  key={annotation.img_id || index}
                  align="center"
                  justify="between"
                  className={twMerge([
                    "cursor-pointer p-2 border-b border-[var(--gray-a4)] hover:bg-[var(--gray-a3)]",
                    annotation.img_id &&
                      selectedChangeGroup?.img_id === annotation.img_id &&
                      "bg-[var(--gray-a4)] hover:bg-[var(--gray-a4)]",
                  ])}
                  onClick={() => {
                    setSelectedChangeGroup(annotation);
                    setSelectedIndex(index);
                  }}
                >
                  <Flex direction="column" gap="0">
                    <InfoItem label="Image ID" value={annotation.img_id} />
                    <InfoItem
                      label="Prediction count"
                      value={calcPredictionsCount(annotation.img_id)}
                    />
                    <InfoItem
                      label="Annotation count"
                      value={calcAnnotationCount(annotation.img_id)}
                    />
                    <InfoItem
                      label="Before image"
                      value={`${baseURL}/${annotation.prev_filename}`}
                      truncate
                    />
                    <InfoItem
                      label="After image"
                      value={`${baseURL}/${annotation.filename}`}
                      truncate
                    />
                  </Flex>
                  <Box pr="3">
                    <Flex gap="4" align="center">
                      <Text as="label" size="2" weight="medium">
                        <Checkbox
                          size="3"
                          mr="1"
                          checked={checkedMapping[annotation.img_id]}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          onCheckedChange={(checked) => {
                            setCheckedMapping({
                              ...checkedMapping,
                              [annotation.img_id]: checked,
                            });
                          }}
                        />
                        Good
                      </Text>
                      <Text as="label" size="2" weight="medium">
                        <Checkbox
                          size="3"
                          mr="2"
                          checked={checkedMapping[annotation.img_id] === false}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          onCheckedChange={(checked) => {
                            setCheckedMapping({
                              ...checkedMapping,
                              [annotation.img_id]: !checked,
                            });
                          }}
                        />
                        Bad
                      </Text>
                    </Flex>
                  </Box>
                </Flex>
              ))}
            </Flex>
          </ScrollArea>
        </aside>
        <main className="grow flex flex-col">
          <header className="flex justify-between gap-4 px-4 py-1 border-b">
            <Flex gap="2">
              <LegendToggle
                checked={visibility.prediction}
                color={Colors.PREDICTION_STROKE}
                onCheckedChange={onCheckedChange("prediction")}
              >
                P {filteredPredictionCount()}
              </LegendToggle>
              <LegendToggle
                checked={visibility.annotation}
                color={Colors.ANNOTATION_STROKE}
                onCheckedChange={onCheckedChange("annotation")}
              >
                GT {filteredAnnotationCount()}
              </LegendToggle>
            </Flex>

            <Flex align="center" gap="2">
              <Input
                label="Take confidence"
                className="min-w-[150px] h-[40px]"
                type="number"
                step="0.1"
                max={1}
                min={0}
                value={takeConfidence}
                onChange={(e) => {
                  setTakeConfidence(e.target.value);
                }}
              />
              <Input
                label="Put confidence"
                className="min-w-[150px] h-[40px]"
                type="number"
                step="0.1"
                max={1}
                min={0}
                value={putConfidence}
                onChange={(e) => {
                  setPutConfidence(e.target.value);
                }}
              />

              <Flex
                className={twMerge(
                  "text-white rounded-xl px-5 absolute z-20 text-[3.3rem] left-1/2 top-1 -translate-x-1/2 w-auto justify-center",
                  isBefore ? "bg-[var(--gray-10)]" : "bg-[var(--blue-9)]"
                )}
              >
                {isBefore ? "Before (TAKE)" : "After (PUT)"}
              </Flex>
            </Flex>
          </header>
          <Flex
            ref={containerRef}
            id="canvas"
            justify="center"
            direction="column"
            className="relative grow bg-black"
          >
            <Canvas2DWrapper
              size={canvasDimensions}
              onResize={setCanvasDimensions}
            >
              {selectedChangeGroup && (
                <>
                  <Group opacity={isBefore ? 1 : 0}>
                    <CanvasImage
                      src={`${baseURL}${selectedChangeGroup?.prev_filename}`}
                      width={canvasDimensions.width}
                      height={canvasDimensions.height}
                    />
                  </Group>
                  <Group opacity={!isBefore ? 1 : 0}>
                    <CanvasImage
                      src={`${baseURL}${selectedChangeGroup?.filename}`}
                      width={canvasDimensions.width}
                      height={canvasDimensions.height}
                    />
                  </Group>

                  <Group
                    opacity={isBefore && visibility.annotation ? 1 : 0}
                    listening={isBefore && visibility.annotation}
                  >
                    {annotationTakeTransactions.map((output, index) => (
                      <AnnotationBBox
                        key={`${selectedChangeGroup.img_id}-atake-${index}`}
                        bbox={output.bbox}
                        downscalePoint={downscalePoint}
                        stroke={Colors.ANNOTATION_STROKE}
                        fill={Colors.ANNOTATION_FILL}
                        type="GT Take"
                      />
                    ))}
                  </Group>

                  <Group
                    opacity={!isBefore && visibility.annotation ? 1 : 0}
                    listening={!isBefore && visibility.annotation}
                  >
                    {annotationPutTransactions.map((output, index) => (
                      <AnnotationBBox
                        key={`${selectedChangeGroup.img_id}-aput-${index}`}
                        bbox={output.bbox}
                        downscalePoint={downscalePoint}
                        stroke={Colors.ANNOTATION_STROKE}
                        fill={Colors.ANNOTATION_FILL}
                        type="GT Put"
                      />
                    ))}
                  </Group>

                  <Group
                    opacity={!isBefore && visibility.prediction ? 1 : 0}
                    listening={!isBefore && visibility.prediction}
                  >
                    {filteredPutPredictions.map((output, index) => (
                      <PredictionBBox
                        key={`${selectedChangeGroup.img_id}-ptake-${index}`}
                        bbox={output.bbox}
                        downscalePoint={downscalePoint}
                        stroke={Colors.PREDICTION_STROKE}
                        fill={Colors.PREDICTION_FILL}
                        type="P Put"
                        confidence={output.score}
                      />
                    ))}
                  </Group>

                  <Group
                    opacity={isBefore && visibility.prediction ? 1 : 0}
                    listening={isBefore && visibility.prediction}
                  >
                    {filteredTakePredictions.map((output, index) => (
                      <PredictionBBox
                        key={`${selectedChangeGroup.img_id}-pput-${index}`}
                        bbox={output.bbox}
                        downscalePoint={downscalePoint}
                        stroke={Colors.PREDICTION_STROKE}
                        fill={Colors.PREDICTION_FILL}
                        type="P Take"
                        confidence={output.score}
                      />
                    ))}
                  </Group>
                </>
              )}
            </Canvas2DWrapper>
          </Flex>
        </main>
      </Flex>
    </Flex>
  );
}
